import { type Executions, executionsConst, executionsUtils } from 'features/executions'
import type { Step } from 'features/campaigns/types'
import {
	durationParser,
	Param,
	URLSearchParser,
	valueToCircle
} from 'shared/search-adapters'

export interface TagItem {
	clear: () => void
	key: number | string
	text: string
}

const GENERICS: Partial<Record<Param, string>> = {
	[Param.Avatar]: 'avatar',
	[Param.Campaigns]: 'campaigns',
	[Param.Created]: 'created',
	[Param.Distance]: 'connections',
	[Param.ExecutionDistance]: 'connections',
	[Param.ExecutionLocations]: 'locations',
	[Param.ExecutionPremium]: 'premium',
	[Param.ExecutionStatus]: 'state',
	[Param.GotAnswer]: 'contact replied ',
	[Param.LastContacted]: 'contacted',
	[Param.LinkedInQualification]: 'qualification',
	[Param.Locations]: 'locations',
	[Param.NotAvatar]: 'NOT: avatar',
	[Param.NotCampaigns]: 'NOT: campaigns',
	[Param.NotContacted]: 'NOT: contacted',
	[Param.NotCreated]: 'NOT: created',
	[Param.NotLinkedInQualification]: 'NOT: qualification',
	[Param.Premium]: 'premium'
}

export function getTagText(
	params: URLSearchParams,
	key: Param,
	steps?: Step[] | null
): string {
	switch (key) {
		case Param.Avatar:
		case Param.NotAvatar: {
			const value = params.get(key)
			if (value === '1') {
				return `${GENERICS[key]}: Yes`
			} else if (value === '2') {
				return `${GENERICS[key]}: No`
			}
			return ''
		}
		case Param.Created:
		case Param.LastContacted:
		case Param.NotContacted:
		case Param.NotCreated: {
			const formatted = durationParser
				.parse(params, key)
				?.map(date => date.format('DD MMM YYYY'))

			return formatted ? `${GENERICS[key]}: ${formatted.join(' — ')}` : ''
		}

		case Param.ExecutionPremium:
		case Param.Premium: {
			const isValid = URLSearchParser.Boolean(params, key)
			return isValid ? GENERICS[key]! : ''
		}

		case Param.GotAnswer: {
			const value = URLSearchParser.Integer(params, key)
			const text = value ? 'contact replied' : ''
			const isNot = value === 2
			return isNot ? `NOT: ${text}` : text
		}
	}

	let values: string[] = []
	switch (key) {
		case Param.Campaigns:
		case Param.NotCampaigns:
			values = URLSearchParser.Campaigns(params, key).map(item => item.name)
			break
		case Param.LinkedInQualification:
		case Param.ModalLinkedInQualification: {
			const qualifications = URLSearchParser.QualificationValues(
				params,
				key === Param.ModalLinkedInQualification
			)
			values = qualifications.map(
				value => executionsConst.qualificationInfo[value].label
			)
			break
		}
		case Param.NotLinkedInQualification: {
			const qualifications = URLSearchParser.Array(params, key)
			values = qualifications.map(
				// @ts-ignore
				value => executionsConst.qualificationInfo[value]?.label || value
			)
			break
		}

		default:
			values = URLSearchParser.Array(params, key)
	}

	return getArrayText(values, key, steps)
}

function getArrayText(
	values: string[],
	key: Param,
	steps?: Step[] | null
): string {
	if (values.length === 1) {
		return transformValue(values[0], key, steps)
	}

	if (values.length > 1) {
		const prefix = GENERICS[key] ?? key
		return `${prefix} (${values.length})`
	}

	return ''
}

function transformValue(value: string, key: Param, steps?: Step[] | null) {
	switch (key) {
		case Param.Distance:
		case Param.ExecutionDistance: {
			const circle = valueToCircle(value)
			return circle ? circle.label : ''
		}

		case Param.ExecutionStatus: {
			return executionsUtils.getExecutionTitle(value as Executions.CampaignExecutionState, steps)
		}

		case Param.NotCampaigns: {
			return `NOT: ${value}`
		}

		case Param.NotLinkedInQualification: {
			return `NOT: ${value}`
		}

		default:
			return value
	}
}
